/**
 * Funciones de validación para formularios
 * Cada función retorna un mensaje de error o null si es válido
 */

import {
  REGEX_SOLO_LETRAS,
  REGEX_TELEFONO_10_DIGITOS,
  REGEX_NUMERO_REPETITIVO,
  LIMITES,
  MENSAJES_ERROR,
} from './validationRules';
import { montoToNumber } from './sanitizers';

// ============================================================================
// VALIDADORES DE TEXTO
// ============================================================================

/**
 * Valida el nombre completo
 * @param nombre Nombre a validar
 * @returns Mensaje de error o null si es válido
 */
export const validateNombreCompleto = (nombre: string): string | null => {
  const trimmedNombre = nombre.trim();

  if (!trimmedNombre) {
    return MENSAJES_ERROR.NOMBRE.REQUERIDO;
  }

  if (trimmedNombre.length < LIMITES.NOMBRE.MIN_LENGTH) {
    return MENSAJES_ERROR.NOMBRE.MIN_LENGTH;
  }

  if (trimmedNombre.length > LIMITES.NOMBRE.MAX_LENGTH) {
    return MENSAJES_ERROR.NOMBRE.MAX_LENGTH;
  }

  if (!REGEX_SOLO_LETRAS.test(trimmedNombre)) {
    return MENSAJES_ERROR.NOMBRE.SOLO_LETRAS;
  }

  return null;
};

/**
 * Valida el número de teléfono
 * @param telefono Número de teléfono a validar
 * @returns Mensaje de error o null si es válido
 */
export const validateTelefono = (telefono: string): string | null => {
  const trimmedTelefono = telefono.trim();

  if (!trimmedTelefono) {
    return MENSAJES_ERROR.TELEFONO.REQUERIDO;
  }

  if (!REGEX_TELEFONO_10_DIGITOS.test(trimmedTelefono)) {
    return MENSAJES_ERROR.TELEFONO.DIGITOS;
  }

  if (REGEX_NUMERO_REPETITIVO.test(trimmedTelefono)) {
    return MENSAJES_ERROR.TELEFONO.REPETITIVO;
  }

  return null;
};

// ============================================================================
// VALIDADORES DE NÚMEROS
// ============================================================================

/**
 * Valida el tamaño del grupo
 * @param tamano Tamaño del grupo
 * @returns Mensaje de error o null si es válido
 */
export const validateTamanoGrupo = (tamano: number): string | null => {
  if (tamano < LIMITES.TAMANO_GRUPO.MIN || tamano > LIMITES.TAMANO_GRUPO.MAX) {
    return MENSAJES_ERROR.TAMANO_GRUPO.RANGO;
  }

  return null;
};

/**
 * Valida el monto grupal
 * @param montoFormateado Monto con formato (ej: "10,000")
 * @returns Mensaje de error o null si es válido
 */
export const validateMontoGrupal = (montoFormateado: string): string | null => {
  if (!montoFormateado.trim()) {
    return MENSAJES_ERROR.MONTO_GRUPAL.REQUERIDO;
  }

  const montoNumerico = montoToNumber(montoFormateado);

  if (isNaN(montoNumerico) || montoNumerico <= 0) {
    return MENSAJES_ERROR.MONTO_GRUPAL.POSITIVO;
  }

  if (montoNumerico < LIMITES.MONTO_GRUPAL.MIN) {
    return MENSAJES_ERROR.MONTO_GRUPAL.MIN;
  }

  if (montoNumerico > LIMITES.MONTO_GRUPAL.MAX) {
    return MENSAJES_ERROR.MONTO_GRUPAL.MAX;
  }

  if (montoNumerico % LIMITES.MONTO_GRUPAL.MULTIPLO !== 0) {
    return MENSAJES_ERROR.MONTO_GRUPAL.MULTIPLO;
  }

  return null;
};

// ============================================================================
// VALIDADORES DE HISTORIAL CREDITICIO
// ============================================================================

/**
 * Valida la financiera (solo requerida si tiene historial)
 * @param financiera Nombre de la financiera
 * @param tieneHistorial Si el cliente tiene historial crediticio
 * @returns Mensaje de error o null si es válido
 */
export const validateFinanciera = (
  financiera: string | undefined,
  tieneHistorial: boolean
): string | null => {
  if (tieneHistorial && !financiera) {
    return MENSAJES_ERROR.FINANCIERA.REQUERIDO;
  }

  return null;
};

/**
 * Valida los ciclos completados
 * @param ciclos Número de ciclos completados
 * @param tieneHistorial Si el cliente tiene historial crediticio
 * @returns Mensaje de error o null si es válido
 */
export const validateCiclos = (
  ciclos: number | undefined,
  tieneHistorial: boolean
): string | null => {
  if (tieneHistorial) {
    if (!ciclos || ciclos < LIMITES.CICLOS.MIN) {
      return MENSAJES_ERROR.CICLOS.MIN;
    }

    if (ciclos < LIMITES.CICLOS.MIN_CON_HISTORIAL) {
      return MENSAJES_ERROR.CICLOS.MIN_CON_HISTORIAL;
    }
  }

  return null;
};
